import { AxiosPromise } from 'axios';
import axios from '@/axios';
import { NewProposalRequest } from '@/model/request/new-proposal.request';
import { ProposalCustomer } from '@/model/proposal-customer';
import { ProposalSalePrice } from '@/model/proposal-sale-price';
import { ProposalCostPrice } from '@/model/proposal-cost-price';
import { UpdateProposalRequest } from '@/model/request/update-proposal.request';
import { ProposalExportType } from '@/model/proposal-export-type';
import { ProposalStatus } from '@/model/proposal-status';
import { SupplierParams } from '@/model/suppliers-choices';
import { ProposalChangelog } from '@/model/proposal-changelog';

const proposalsService = {
    create(customerId: string, proposal: NewProposalRequest): AxiosPromise<ProposalSalePrice> {
        return axios.post(`/customers/${customerId}/proposals`, proposal)
    },
    update(proposalId: string, data: UpdateProposalRequest): AxiosPromise<ProposalSalePrice> {
        return axios.put(`/proposals/${proposalId}`, data);
    },
    getList(includeArchived = false): AxiosPromise<ProposalCustomer[]> {
        return axios.get(`/proposals`, { params: { includeArchived } })
    },
    repopulate(proposalId: string): AxiosPromise<ProposalSalePrice> {
      return axios.post(`/proposals/${proposalId}/repopulate`)
    },
    destroy(proposalId: string): AxiosPromise<void> {
      return axios.delete(`/proposals/${proposalId}`);
    },
    getCostPrices(customerId: string, selectedSuppliers: SupplierParams[]): AxiosPromise<ProposalCostPrice> {
        return axios.get(
            `/customers/${customerId}/proposals/new`,
            {
                params: {
                    suppliers: selectedSuppliers,
                }
            })
    },
    getOne(proposalId: string): AxiosPromise<ProposalSalePrice> {
        return axios.get(`/proposals/${proposalId}`);
    },
    getForReuse(proposalId: string, loadAllProducts: boolean, keepSalePrices: boolean): AxiosPromise<ProposalSalePrice> {
      return axios.get(`/proposals/${proposalId}/reuse?load_all_available_products=${loadAllProducts}&keep_prices=${keepSalePrices}`);
    },
    export(proposalId: string, type: ProposalExportType, forImport = false): AxiosPromise<string> {
        const params = forImport ? { for_import: 'true' } : {};
        return axios.get(`/proposals/${proposalId}/export/${type}`, {
            responseType: 'blob',
            params
        });
    },
    changeStatus(proposalId: string, status: ProposalStatus): AxiosPromise<ProposalSalePrice> {
        return axios.put(`/proposals/${proposalId}/change-status/${status}`);
    },
    updateDescription(proposalId: string, description: string): AxiosPromise<void> {
        return axios.put(`/proposals/${proposalId}/description`, { description });
    },
    getChangelog(proposalId: string): AxiosPromise<ProposalChangelog> {
        return axios.get(`/proposals/${proposalId}/changelog`);
    },
    import(proposalId: string, file: File): AxiosPromise<{ success: boolean; message: string; errors: string[]; updated_count: number }> {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post(`/proposals/${proposalId}/import`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    getAvailableSurcharges(proposalId: string): AxiosPromise {
        return axios.get(`/proposals/${proposalId}/available-surcharges`);
    },
    createSurcharge(proposalId: string, data: {
        name: string;
        supplierId: string;
        serviceName?: string;
        countryId?: string;
        priceCents: number;
        grossMargin: number;
    }): AxiosPromise {
        return axios.post(`/proposals/${proposalId}/surcharges`, data);
    }
}

export default proposalsService;
